import { MfeConfig, MfeLoad } from '@carvana/microfrontend';
import { MfeReact } from '@carvana/microfrontend-react';
import Cookies from 'js-cookie';
import { useEffect } from 'react';
import { mfeCacheBuster } from '|/helpers/mfe-cache-buster';

export const MFE_DEV_OVERRIDE_COOKIE = 'cvna-local-verifx';
export const MFE_DEV_DEFAULT_URL = 'http://localhost:3002/verifx/';
export const MFE_FEATURE_BRANCH_COOKIE = 'cvna-verifx-feature-branch';

export const OPD_DEV_OVERRIDE_COOKIE = 'cvna-local-opd';
export const OPD_DEV_DEFAULT_URL = 'http://localhost:3002/orderplaceddashboard/';
export const OPD_FEATURE_BRANCH_COOKIE = 'cvna-opd-feature-branch';

const sanitizeBranchName = (name: string): string => name.replace(/[^a-zA-Z0-9]/g, '-');

const loadReactJsxRuntime = async () => {
  const [jsxRuntime, jsxDevRuntime] = await Promise.all([
    import('react/jsx-runtime'),
    import('react/jsx-dev-runtime')
  ]);

  return {
    ...jsxRuntime,
    ...jsxDevRuntime
  };
};

const getVerifxMfeManifestUrl = (): string | null => {
  if (process.env.NODE_ENV !== 'production' && Cookies.get(MFE_DEV_OVERRIDE_COOKIE) === 'true') {
    console.log(
      '%c[MFE DEV MODE] Verifx MFE loading from: ' + MFE_DEV_DEFAULT_URL,
      'background: #ff6b6b; color: white; padding: 4px 8px; font-weight: bold;'
    );
    return `${MFE_DEV_DEFAULT_URL}assets-manifest.json`;
  }

  const featureBranch = Cookies.get(MFE_FEATURE_BRANCH_COOKIE);
  if (process.env.NODE_ENV !== 'production' && featureBranch) {
    const sanitizedBranch = sanitizeBranchName(featureBranch);
    const manifestUrl = `${process.env.CDN_URL}/purchase-verifications-module/features/assets-manifest-${sanitizedBranch}.json?v=${Date.now()}`;
    console.log(
      '%c[MFE FEATURE BRANCH] Verifx MFE loading from branch: ' + featureBranch,
      'background: #6b6bff; color: white; padding: 4px 8px; font-weight: bold;'
    );
    return manifestUrl;
  }

  return null;
};

const useGlobalMfeConfig = () => {
  useEffect(() => {
    MfeConfig({
      name: 'purchase-ui',
      ENV: process.env.LOCAL === 'true' ? 'local' : process.env.NODE_ENV,
      debug: process.env.NODE_ENV !== 'production',
      Render: MfeReact(),
      globals: {
        React: () => import('react'),
        ReactDOM: () => import('react-dom'),
        // Required by React 19 MFEs using @carvana/microfrontend-webpack's
        // JSX runtime shim while embedded in checkout's React 18 shell.
        JSXRuntime: loadReactJsxRuntime,
        styled: () => import('styled-components'),
        'styled-components': () => import('styled-components'),
        StyledComponents: () => import('styled-components'),
        PropTypes: () => import('prop-types'),
        'prop-types': () => import('prop-types'),
        // Required by AppraisalMFE (Carvana.STC.Appraisal.UI) — see its
        // webpack.module.js externals list.
        Axios: () => import('axios'),
        ReactRouterDom: () => import('react-router-dom'),
        datadog: () => import('@datadog/browser-rum')
      },
      files: [
        [
          'HeaderModule',
          [
            {
              component: 'default',
              default: {
                file: 'index.js',
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
                manifest: `${process.env.CDN_URL}/header-module/header/assets-manifest.json?${mfeCacheBuster()}`
              }
            }
          ]
        ],
        [
          'CoreSupportDropdown',
          [
            {
              component: 'default',
              default: {
                file: 'index.js',
                manifest: `${process.env.SUPPORT_AND_CONTACT_BASE_URL}/support-dropdown-module/assets-manifest.json`
              }
            }
          ]
        ],
        [
          'SoftPullModal',
          [
            {
              component: 'default',
              chunks: ['styles.css'],
              default: {
                file: 'index.js',
                manifest: `${process.env.CDN_URL}/softpull-modal-module/assets-manifest.json?${mfeCacheBuster()}`
              }
            }
          ]
        ],
        [
          'AppraisalMFE',
          [
            {
              component: 'default',
              default: {
                file: 'index.js',
                manifest: `${process.env.CDN_URL}/stc-appraisal-ui/modules/assets-manifest.json?${mfeCacheBuster()}`
              }
            }
          ]
        ],
        [
          'CustomerRequestContentMFE',
          [
            {
              component: 'default',
              chunks: ['styles.css'],
              default: {
                file: 'index.js',
                manifest: `${process.env.CDN_URL}/customer-components/mfe/customer-request-content/assets-manifest.json?${mfeCacheBuster()}`
              }
            }
          ]
        ]
      ]
    }).catch(() => {
      /* do nothing */
    });
  }, []);
};

/**
 * Loads the Verifx MFE (PurchaseVerificationsModule) on demand.
 * Call this function when you want the MFE to load (e.g., after page renders).
 */
export const loadVerifxMfe = () => {
  const VERIFX_MFE_MANIFEST_URL = getVerifxMfeManifestUrl();
  return MfeLoad({
    files: [
      [
        'PurchaseVerificationsModule',
        [
          {
            component: 'default',
            chunks: ['index.css'],
            default: {
              file: 'index.js',
              manifest:
                VERIFX_MFE_MANIFEST_URL ??
                `${process.env.CDN_URL}/purchase-verifications-module/assets-manifest.json?${mfeCacheBuster()}`
            }
          }
        ]
      ]
    ]
  }).catch(() => {
    const featureBranch = Cookies.get(MFE_FEATURE_BRANCH_COOKIE);
    if (featureBranch) {
      console.warn(
        `[MFE FEATURE BRANCH] Failed to load branch: ${featureBranch}. Check that the branch exists and was built.`
      );
    }
  });
};

const getOpdMfeManifestUrl = (): string | null => {
  if (process.env.NODE_ENV !== 'production' && Cookies.get(OPD_DEV_OVERRIDE_COOKIE) === 'true') {
    console.log(
      '%c[MFE DEV MODE] OPD MFE loading from: ' + OPD_DEV_DEFAULT_URL,
      'background: #ff6b6b; color: white; padding: 4px 8px; font-weight: bold;'
    );
    return `${OPD_DEV_DEFAULT_URL}assets-manifest.json`;
  }

  const featureBranch = Cookies.get(OPD_FEATURE_BRANCH_COOKIE);
  if (process.env.NODE_ENV !== 'production' && featureBranch) {
    const sanitizedBranch = sanitizeBranchName(featureBranch);
    const manifestUrl = `${process.env.CDN_URL}/orderplaceddashboard/features/assets-manifest-${sanitizedBranch}.json?v=${Date.now()}`;
    console.log(
      '%c[MFE FEATURE BRANCH] OPD MFE loading from branch: ' + featureBranch,
      'background: #6b6bff; color: white; padding: 4px 8px; font-weight: bold;'
    );
    return manifestUrl;
  }

  return null;
};

export const loadOpdMfe = () => {
  const OPD_MFE_MANIFEST_URL = getOpdMfeManifestUrl();
  return MfeLoad({
    files: [
      [
        'OrderPlacedDashboardModule',
        [
          {
            component: 'default',
            chunks: ['index.css'],
            default: {
              file: 'index.js',
              manifest:
                OPD_MFE_MANIFEST_URL ??
                `${process.env.CDN_URL}/orderplaceddashboard/assets-manifest.json?${mfeCacheBuster()}`
            }
          }
        ]
      ]
    ]
  }).catch(() => {
    const featureBranch = Cookies.get(OPD_FEATURE_BRANCH_COOKIE);
    if (featureBranch) {
      console.warn(
        `[MFE FEATURE BRANCH] Failed to load branch: ${featureBranch}. Check that the branch exists and was built.`
      );
    }
  });
};

export default useGlobalMfeConfig;
